import { Box, Flex, Spacer, VStack, useDisclosure } from '@chakra-ui/react';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FEATURE_FLAGS } from '@bower/shared/constants/featureFlags';
import { useFeatureFlag } from '../../../hooks/analytics/useFeatureFlag';
import { useIsReadOnly } from '../../../hooks/user/useWorkspaceRole';
import { useCanUseBird } from '../../../hooks/user/useCanUseBird';
import { openBirdChat } from '../../../services/events/birdChatEvents';
import InviteTeamMemberDialog from '../../dialogs/InviteTeamMemberDialog';
import { CreateMenu } from './CreateMenu';
import { NavRailFooter } from './NavRailFooter';
import { NavRailIcon } from './NavRailIcon';
import { NavRailItem } from './NavRailItem';
import { WorkspaceMenu } from './WorkspaceMenu';
import { NAV_RAIL_WIDTH } from './navRailWidth';
import { useChecklistPanel } from './useChecklistPanel';
import { ROUTES } from '../../../constants/routes';
import { useOpenCreate } from '../../../hooks/create/useOpenCreate';
import type { LeftPanel, LeftPanelSlot } from '../useLeftPanel';

interface NavRailProps {
  /** Which panel currently holds the exclusive left-panel slot. */
  activePanel: LeftPanel;
  /** Asking for the active panel closes it — see `useLeftPanel`. */
  onTogglePanel: (panel: LeftPanelSlot) => void;
  /**
   * The Workspace item navigates to the workspace root instead of toggling a
   * panel, and calls this first so the shell can expand a collapsed sidebar
   * (and take the slot back from Bower) for the page it lands on. See
   * `useLeftPanel`'s `showWorkspace`.
   */
  onShowWorkspace: () => void;
  /**
   * The user is viewing the workspace (`useInWorkspaceContext`, computed once
   * by the shell), which makes the Workspace item current.
   */
  inWorkspace: boolean;
}

/**
 * The navigation rail, fixed at `NAV_RAIL_WIDTH`: Home, Workspace,
 * Protocols, Chat. It never expands — the Workspace item navigates to the
 * workspace and the sidebar follows the workspace context — so every target
 * can carry a visible label and needs no tooltip. Search lives in the top bar.
 *
 * Chat toggles the ONE left-panel slot, so the rail takes the slot's value
 * rather than a boolean per panel.
 *
 * Create and the checklist's create CTAs open the one Create modal (mounted
 * by Layout); the rail owns only the invite dialog.
 */
export function NavRail({
  activePanel,
  onTogglePanel,
  onShowWorkspace,
  inWorkspace,
}: NavRailProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isReadOnlyRole = useIsReadOnly();
  const canUseBird = useCanUseBird();
  const liveCallEnabled = useFeatureFlag(FEATURE_FLAGS.LIVE_CALL_PREVIEW_ENABLED);
  const isBirdOpen = activePanel === 'bird';
  // OPENS go through `openBirdChat` like every other Bower entry point, because
  // that listener (useBirdPanel) is where `Bower Chat Opened` is tracked with its
  // `source` dimension — a direct slot toggle would open the panel without ever
  // recording the app's primary chat affordance. CLOSES stay a direct toggle:
  // the event path only opens.
  //
  // Via the helper rather than a hand-written `dispatchEvent`: the event name is
  // an unchecked string literal where a typo fails silently, and any future
  // dispatch-side default (as `freshContext` already is) reaches every entry
  // point through it — see birdChatEvents.ts.
  const toggleBird = useCallback(() => {
    if (isBirdOpen) {
      onTogglePanel('bird');
      return;
    }
    openBirdChat({ source: 'nav_rail' });
  }, [isBirdOpen, onTogglePanel]);

  const inviteDialog = useDisclosure();
  const openCreate = useOpenCreate();

  // The checklist panel is a fixed overlay at the rail's right edge, so it lands
  // on top of whatever holds the slot. It closes Bower specifically — routed
  // through the slot's own toggle, so it can't desynchronise from it.
  const { showChecklist, checklistData, isPanelOpen, handleOpenPanel, closePanel } =
    useChecklistPanel({
      isBirdOpen,
      onToggleBird: toggleBird,
      onOpenInvite: inviteDialog.onOpen,
    });

  return (
    <Flex
      direction="column"
      w={`${NAV_RAIL_WIDTH}px`}
      h="full"
      // Borderless and transparent: the rail sits DIRECTLY on the shell's
      // parchment frame (2026-09 design). It used to paint `sidebar.bg` with a
      // `sidebar.border` hairline, which against the new frame read as a
      // second, slightly-wrong panel edge butting into the curved canvas.
      // `sidebar.item.*` still drives the per-item states in NavRailItem.
      bg="transparent"
      flexShrink={0}
    >
      <Box
        // Matches the curved canvas's top inset in Layout (12px from md up),
        // so the logo's top edge lines up with the canvas's top edge.
        pt={{ base: 1, md: '12px' }}
        pb={4}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* The top slot is the workspace: whose lab this is, and the door to
            switching it. Home is the first item below. Its gap to Create is
            wider than the rail's own item spacing, so the logo reads as a
            header rather than the first button. */}
        <WorkspaceMenu />
      </Box>

      <Box as="nav" aria-label="Primary" px={1}>
        <VStack spacing={1} align="center">
          {/* Hidden for viewers, who can't create content (the agent-service +
              write routes reject them anyway). */}
          {!isReadOnlyRole && (
            <CreateMenu onOpen={() => openCreate({ surface: 'sidebar_create_menu' })} />
          )}

          {/* Home leads (2026-09 rail order). */}
          <NavRailItem
            label="Home"
            tooltip="Home: your dashboard and recent work"
            icon={<NavRailIcon name="home" />}
            isActive={location.pathname === ROUTES.dashboard}
            onClick={() => navigate(ROUTES.dashboard)}
            testId="rail-home"
          />

          {/* The workspace IS a page. The item navigates to its root (never
              toggles the sidebar, which follows the workspace context on its
              own) and is current wherever the user is viewing the workspace,
              an artifact opened from it included. */}
          <NavRailItem
            label="Workspace"
            tooltip="Browse collections, artifacts, protocols and equipment"
            icon={<NavRailIcon name="folder" />}
            isActive={inWorkspace}
            onClick={() => {
              onShowWorkspace();
              navigate(ROUTES.collections);
            }}
            testId="rail-workspace"
          />

          {/* Directly after Workspace, whose own tooltip already reads "Browse
              collections, artifacts, protocols and equipment": this is a
              shortcut into that same content domain, so it belongs beside it
              rather than splitting Home from Workspace. */}
          <NavRailItem
            label="Protocols"
            tooltip="Your lab's protocols: methods and procedures"
            icon={<NavRailIcon name="science" />}
            // Stays lit on a protocol's OWN page, not just the index: this is
            // a section target, and dropping the highlight on `/protocols/:id`
            // tells the reader they have left a section they are still in.
            // The trailing slash matters — a bare `startsWith` would also
            // claim a future sibling route like `/protocols-archive`.
            isActive={
              location.pathname === ROUTES.protocols ||
              location.pathname.startsWith(`${ROUTES.protocols}/`)
            }
            onClick={() => navigate(ROUTES.protocols)}
            testId="rail-protocols"
          />

          {canUseBird && (
            <NavRailItem
              label="Chat"
              tooltip={isBirdOpen ? 'Close the chat panel' : 'Chat with Bower about your research'}
              icon={<NavRailIcon name="chat_bubble" />}
              isActive={isBirdOpen}
              isToggle
              onClick={toggleBird}
              testId="rail-chat"
            />
          )}

          {/* Internal LiveKit call preview, last in the rail. Only while
              `live-call-preview-enabled` is on (the page is NotFound otherwise).
              It joins the room set in Workspace settings. */}
          {liveCallEnabled && (
            <NavRailItem
              label="Call"
              tooltip="Start or join a call"
              icon={<NavRailIcon name="call" />}
              isActive={location.pathname === ROUTES.liveCall}
              onClick={() => navigate(ROUTES.liveCall)}
              testId="rail-call"
            />
          )}
        </VStack>
      </Box>

      <Spacer />

      <NavRailFooter
        showChecklist={showChecklist}
        checklistData={checklistData}
        isChecklistPanelOpen={isPanelOpen}
        onOpenChecklistPanel={handleOpenPanel}
        onCloseChecklistPanel={closePanel}
      />

      {/* The rail's invite dialog, for the checklist's "invite a teammate" CTA,
          which arrives as a window CustomEvent only the rail listens for. The
          workspace menu sends invites to Settings' Members tab instead. */}
      <InviteTeamMemberDialog isOpen={inviteDialog.isOpen} onClose={inviteDialog.onClose} />
    </Flex>
  );
}
